import React, { useState, useEffect } from "react";
import { useApp, REJECT_REASONS } from "../context/AppContext";
import { getEvents, getUsers } from "../services/api";

const F = "'Montserrat',sans-serif";

export default function DecisionModal() {
  const { decisionModal, setDecisionModal, updateReviewStatus, showToast } = useApp();
  const [reason, setReason]       = useState(null);
  const [note, setNote]           = useState("");
  const [referForm, setReferForm] = useState({ targetEvent: "", targetOwner: "" });
  const [events, setEvents]       = useState([]);
  const [users, setUsers]         = useState([]);

  useEffect(() => {
    if (!decisionModal) return;
    setReason(null);
    setNote("");
    setReferForm({ targetEvent: "", targetOwner: "" });
    if (decisionModal.kind === "refer") {
      getEvents().then(d => { if (Array.isArray(d)) setEvents(d); }).catch(() => {});
      getUsers().then(d  => { if (Array.isArray(d)) setUsers(d);  }).catch(() => {});
    }
  }, [decisionModal?.asgnId, decisionModal?.coId, decisionModal?.status]);

  if (!decisionModal) return null;

  const { asgnId, coId, coName, status, kind, showName } = decisionModal;

  const close = () => setDecisionModal(null);

  const canConfirm =
    (kind === "reject"   && !!reason) ||
    (kind === "research" && !!note.trim()) ||
    (kind === "refer"    && !!referForm.targetEvent && !!referForm.targetOwner);

  const confirm = () => {
    if (kind === "reject") {
      const r = REJECT_REASONS.find(x => x.code === reason);
      updateReviewStatus(asgnId, coId, status, { rejectReason: r?.label, rejectSignal: r?.signal, decisionNote: note || "" }, true);
      showToast(`${coName} marked ${status}${r ? ` — ${r.label}` : ""}`);
    } else if (kind === "research") {
      updateReviewStatus(asgnId, coId, status, { decisionNote: note }, true);
      showToast(`${coName} marked Needs Research`);
    } else if (kind === "refer") {
      updateReviewStatus(asgnId, coId, status, {}, true);
      showToast(`${coName} referred to ${referForm.targetOwner} for ${referForm.targetEvent}`);
    }
    close();
  };

  const confirmColor = kind === "reject" ? "#C1121F" : kind === "refer" ? "#E8431A" : "#9a5b00";

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(15,23,42,0.45)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:500, fontFamily:F }}
      onClick={close}>
      <div onClick={e => e.stopPropagation()}
        style={{ background:"#fff", borderRadius:14, width:440, padding:"24px", boxShadow:"0 24px 60px rgba(16,24,40,0.28)" }}>

        {/* Header */}
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:6 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:800, color:"#26262A" }}>
              {kind === "reject" ? "Why is this being rejected?" : kind === "research" ? "What needs research?" : "Refer to Another Event"}
            </div>
            <div style={{ fontSize:11, color:"#AFABAB", marginTop:2 }}>
              {kind === "refer"
                ? <>Referring <strong style={{ color:"#3F3F46" }}>{coName}</strong> from {showName || "this show"}</>
                : kind === "reject"
                ? <>Rejecting <strong style={{ color:"#3F3F46" }}>{coName}</strong> · a reason is required</>
                : <>A note is required for <strong style={{ color:"#3F3F46" }}>{coName}</strong></>}
            </div>
          </div>
          <button onClick={close}
            style={{ width:28, height:28, borderRadius:8, border:"1px solid #F4F4F5", background:"#F4F4F5", cursor:"pointer", fontSize:13, color:"#818084", display:"flex", alignItems:"center", justifyContent:"center" }}>
            ✕
          </button>
        </div>

        {/* Reject fields */}
        {kind === "reject" && (<>
          <div style={{ fontSize:9, fontWeight:700, color:"#AFABAB", textTransform:"uppercase", letterSpacing:0.5, margin:"14px 0 8px" }}>Select a reason</div>
          <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
            {REJECT_REASONS.map(r => {
              const sel = reason === r.code;
              return (
                <button key={r.code} onClick={() => setReason(r.code)}
                  style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", borderRadius:9,
                    border:`1.5px solid ${sel ? "#E8431A" : "#F4F4F5"}`, background:sel ? "#eff4ff" : "#fff",
                    cursor:"pointer", fontFamily:F, textAlign:"left", width:"100%" }}>
                  <span style={{ width:16, height:16, borderRadius:"50%", border:`2px solid ${sel ? "#E8431A" : "#AFABAB"}`,
                    display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    {sel && <span style={{ width:7, height:7, borderRadius:"50%", background:"#E8431A" }}/>}
                  </span>
                  <span style={{ flex:1, fontSize:12, fontWeight:sel ? 700 : 500, color:"#26262A" }}>{r.label}</span>
                  {r.signal !== "—" && (
                    <span style={{ fontSize:8.5, fontWeight:700, color:"#5F1156", background:"#F5EEF2", padding:"2px 8px", borderRadius:10 }}>{r.signal}</span>
                  )}
                </button>
              );
            })}
          </div>
          <div style={{ fontSize:9, fontWeight:700, color:"#AFABAB", textTransform:"uppercase", letterSpacing:0.5, margin:"14px 0 6px" }}>Add detail (optional)</div>
          <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. competitor of an existing key account…"
            style={{ width:"100%", minHeight:60, padding:"10px 12px", borderRadius:9, border:"1px solid #F4F4F5", fontSize:12, fontFamily:F, outline:"none", resize:"vertical", boxSizing:"border-box" }}/>
        </>)}

        {/* Research fields */}
        {kind === "research" && (<>
          <div style={{ fontSize:9, fontWeight:700, color:"#AFABAB", textTransform:"uppercase", letterSpacing:0.5, margin:"14px 0 6px" }}>Note</div>
          <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. need to confirm export markets…"
            style={{ width:"100%", minHeight:60, padding:"10px 12px", borderRadius:9, border:"1px solid #F4F4F5", fontSize:12, fontFamily:F, outline:"none", resize:"vertical", boxSizing:"border-box" }}/>
        </>)}

        {/* Refer fields */}
        {kind === "refer" && (<>
          {[
            { k:"targetEvent", l:"Target ITE Event *", opts: events.map(e => e.name) },
            { k:"targetOwner", l:"Assign To *",        opts: users.filter(u => u.role === "sales_manager").map(u => u.name) },
          ].map(f => (
            <div key={f.k} style={{ marginBottom:14, marginTop:14 }}>
              <label style={{ fontSize:11, fontWeight:600, color:"#3F3F46", display:"block", marginBottom:5 }}>{f.l}</label>
              <select value={referForm[f.k]} onChange={e => setReferForm(p => ({ ...p, [f.k]: e.target.value }))}
                style={{ width:"100%", padding:"9px 12px", borderRadius:7, border:`1px solid ${referForm[f.k] ? "#F3A78C" : "#D8DADC"}`,
                  fontSize:12, fontFamily:F, outline:"none", background:referForm[f.k] ? "#FCEBE5" : "#fff", boxSizing:"border-box" }}>
                <option value="">Select…</option>
                {f.opts.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          ))}
          {referForm.targetEvent && referForm.targetOwner && (
            <div style={{ padding:"10px 12px", background:"#FCEBE5", borderRadius:7, border:"1px solid #FCEBE5", fontSize:10, color:"#C23417", lineHeight:1.5 }}>
              <strong>Preview:</strong> {coName} will be referred to <strong>{referForm.targetOwner}</strong> for <strong>{referForm.targetEvent}</strong>.
            </div>
          )}
        </>)}

        {/* Actions */}
        <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginTop:16 }}>
          <button onClick={close}
            style={{ padding:"9px 16px", borderRadius:8, border:"1px solid #D8DADC", background:"#fff", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:F, color:"#818084" }}>
            Cancel
          </button>
          <button onClick={confirm} disabled={!canConfirm}
            style={{ padding:"9px 22px", borderRadius:8, border:"none", fontSize:12, fontWeight:700,
              cursor: canConfirm ? "pointer" : "default", fontFamily:F, color:"#fff",
              background: canConfirm ? confirmColor : "#D8DADC" }}>
            {kind === "reject" ? "Confirm Rejection" : kind === "refer" ? "Refer →" : "Mark for Research"}
          </button>
        </div>

      </div>
    </div>
  );
}
